import SurveyService from 'src/services/surveys/survey.service';
import type {
  SurveyGroupPreview,
  SurveyGroupPreviewAnswer,
  SurveyGroupPreviewQuestion,
  SurveyGroupResultsPayload,
  SurveyGroupResultsSurveyPayload,
} from 'src/types/survey-group-api.types';
import type {
  AnswerStats,
  QuestionStats,
  SurveyStats,
  SurveyStatsJSON,
  SurveySummaryData,
  SurveySummaryDataJSON,
  SurveySummaryQuestion,
} from 'src/redux/survey/survey.types';
import { SurveyQuestionType } from 'src/redux/survey/survey.types';
import type { CombinedQuestionStats } from './view-survey.types';

function inferQuestionTypeFromStats(question: QuestionStats): SurveyQuestionType {
  if (question.freeformAnswers !== undefined) {
    return SurveyQuestionType.Freeform;
  }
  return SurveyQuestionType.SingleSelect;
}

export function parsePayloadStatsJson(payload: SurveyGroupResultsSurveyPayload): SurveyStats | undefined {
  const raw = payload.stats;
  if (raw == null || typeof raw !== 'object') {
    return undefined;
  }
  const statsJson: SurveyStatsJSON = {
    ...(raw as SurveyStatsJSON),
    ext_survey_id: (raw as SurveyStatsJSON).ext_survey_id ?? payload.ext_survey_id,
  };
  if (!statsJson.questions || (typeof statsJson.questions === 'object' && Object.keys(statsJson.questions).length === 0)) {
    return undefined;
  }
  return SurveyService.parseSurveyStats(statsJson);
}

/** Server-merged group stats from common API cutover (`merged_stats` on the group results payload). */
export function parseMergedGroupStats(
  payload: Pick<SurveyGroupResultsPayload, 'merged_stats' | 'survey_group_id'>,
): SurveyStats | undefined {
  const raw = payload.merged_stats;
  if (raw == null || typeof raw !== 'object') {
    return undefined;
  }
  const statsJson: SurveyStatsJSON = {
    ...(raw as SurveyStatsJSON),
    ext_survey_id: (raw as SurveyStatsJSON).ext_survey_id ?? payload.survey_group_id,
  };
  if (
    !statsJson.questions
    || (typeof statsJson.questions === 'object' && Object.keys(statsJson.questions).length === 0)
  ) {
    return undefined;
  }
  return SurveyService.parseSurveyStats(statsJson);
}

function parsePayloadSummaryJson(raw: unknown): SurveySummaryData | undefined {
  if (raw == null || typeof raw !== 'object') {
    return undefined;
  }
  return SurveyService.parseSurveySummary(raw as SurveySummaryDataJSON);
}

function toCombinedQuestionStats(
  q: QuestionStats,
  summaryQuestion: SurveySummaryQuestion | undefined,
  sourceExtSurveyId?: string
): CombinedQuestionStats {
  const questionType = inferQuestionTypeFromStats(q);
  return {
    ...q,
    hasFeedback: false,
    summaries: summaryQuestion?.summaries,
    questionType,
    ...(sourceExtSurveyId ? { sourceExtSurveyId } : {}),
  };
}

/** Collect stats rows for each mapped question id (each id appears on at most one survey). */
function collectContributorsForCluster(
  parsedByExtSurveyId: Map<string, SurveyStats>,
  mappedQuestionIds: string[]
): Array<{ extSurveyId: string; question: QuestionStats }> {
  const out: Array<{ extSurveyId: string; question: QuestionStats }> = [];
  for (const mqId of mappedQuestionIds) {
    for (const [extSurveyId, surveyStats] of parsedByExtSurveyId) {
      const question = surveyStats.questions.find((x) => x.id === mqId);
      if (question) {
        out.push({ extSurveyId, question });
        break;
      }
    }
  }
  return out;
}

function findAnswerInContributors(
  contributors: Array<{ extSurveyId: string; question: QuestionStats }>,
  answerId: string
): AnswerStats | undefined {
  for (const { question } of contributors) {
    const a = question.answers.find((x) => x.id === answerId);
    if (a) {
      return a;
    }
  }
  return undefined;
}

/** Sum answer rows that belong to one preview answer cluster; recompute percentages vs summed counts. */
function buildMergedAnswersForCluster(
  previewAnswers: SurveyGroupPreviewAnswer[],
  contributors: Array<{ extSurveyId: string; question: QuestionStats }>
): AnswerStats[] {
  const merged: AnswerStats[] = [];

  for (const pa of previewAnswers) {
    const parts: AnswerStats[] = [];
    for (const aid of pa.mapped_answers) {
      const found = findAnswerInContributors(contributors, aid);
      if (found) {
        parts.push(found);
      }
    }
    if (parts.length === 0) {
      continue;
    }
    let totalUsers = 0;
    let aiUsers = 0;
    let humanUsers = 0;
    let rtrUsers = 0;
    let title = pa.default_title ?? '';
    let imageUrl: string | undefined;
    for (const p of parts) {
      totalUsers += p.totalUsers;
      aiUsers += p.aiUsers;
      humanUsers += p.humanUsers;
      rtrUsers += p.rtrUsers;
      if (!imageUrl && p.imageUrl) {
        imageUrl = p.imageUrl;
      }
      if (!title && p.title) {
        title = p.title;
      }
    }
    merged.push({
      ...parts[0],
      id: pa.id,
      title,
      displayName: title,
      imageUrl,
      totalUsers,
      aiUsers,
      humanUsers,
      rtrUsers,
      aiPercentage: 0,
      humanPercentage: 0,
      rtrPercentage: 0,
      totalPercentage: 0,
    });
  }

  const sumTotals = merged.reduce((acc, a) => acc + a.totalUsers, 0);
  if (sumTotals <= 0) {
    return merged;
  }
  return merged.map((a) => ({
    ...a,
    totalPercentage: (a.totalUsers / sumTotals) * 100,
    aiPercentage: (a.aiUsers / sumTotals) * 100,
    humanPercentage: (a.humanUsers / sumTotals) * 100,
    rtrPercentage: (a.rtrUsers / sumTotals) * 100,
  }));
}

function mergeSummariesForCluster(
  contributors: Array<{ extSurveyId: string; question: QuestionStats }>,
  summaryByExtSurveyId: Map<string, SurveySummaryData | undefined>,
  mappedQuestionIds: string[]
): string[] | undefined {
  const texts: string[] = [];
  for (const mqId of mappedQuestionIds) {
    for (const { extSurveyId, question } of contributors) {
      if (question.id !== mqId) {
        continue;
      }
      const summary = summaryByExtSurveyId.get(extSurveyId);
      const row = summary?.questions.find((q) => q.id === mqId);
      if (row?.summaries?.length) {
        texts.push(...row.summaries);
      }
      break;
    }
  }
  return texts.length ? texts : undefined;
}

function mergeClusterQuestions(
  cluster: SurveyGroupPreviewQuestion,
  parsedByExtSurveyId: Map<string, SurveyStats>,
  summaryByExtSurveyId: Map<string, SurveySummaryData | undefined>
): CombinedQuestionStats | undefined {
  const contributors = collectContributorsForCluster(parsedByExtSurveyId, cluster.mapped_questions);
  if (contributors.length === 0) {
    return undefined;
  }

  let uniqueAi = 0;
  let uniqueHuman = 0;
  let aiUsers = 0;
  let humanUsers = 0;
  let rtrUsers = 0;
  let title = cluster.default_title ?? '';
  let displayName = cluster.default_title ?? '';

  const mergedFreeformParts: NonNullable<QuestionStats['freeformAnswers']>[] = [];

  for (const { question } of contributors) {
    uniqueAi += question.unique_ai_users ?? 0;
    uniqueHuman += question.unique_human_users ?? 0;
    aiUsers += question.aiUsers ?? 0;
    humanUsers += question.humanUsers ?? 0;
    rtrUsers += question.rtrUsers ?? 0;
    if (!title && question.title) {
      title = question.title;
    }
    if (!displayName && question.displayName) {
      displayName = question.displayName;
    }
    if (question.freeformAnswers) {
      mergedFreeformParts.push(question.freeformAnswers);
    }
  }

  const mergedTotalUsers = uniqueAi + uniqueHuman;

  const mergedAnswers = buildMergedAnswersForCluster(cluster.answers, contributors);
  const summaries = mergeSummariesForCluster(contributors, summaryByExtSurveyId, cluster.mapped_questions);

  let freeformAnswers: QuestionStats['freeformAnswers'];
  if (mergedFreeformParts.length > 0) {
    freeformAnswers = {
      aiUsers: mergedFreeformParts.flatMap((f) => f.aiUsers),
      humanUsers: mergedFreeformParts.flatMap((f) => f.humanUsers),
      rtrUsers: mergedFreeformParts.flatMap((f) => f.rtrUsers),
    };
  }

  const questionType = freeformAnswers !== undefined
    ? SurveyQuestionType.Freeform
    : cluster.type === 'multi_select'
      ? SurveyQuestionType.MultiSelect
      : SurveyQuestionType.SingleSelect;

  const aiPercentage = mergedTotalUsers > 0 ? (aiUsers / mergedTotalUsers) * 100 : 0;
  const humanPercentage = mergedTotalUsers > 0 ? (humanUsers / mergedTotalUsers) * 100 : 0;
  const rtrPercentage = mergedTotalUsers > 0 ? (rtrUsers / mergedTotalUsers) * 100 : 0;

  const base: CombinedQuestionStats = {
    id: cluster.id,
    title,
    displayName,
    totalUsers: mergedTotalUsers,
    aiUsers,
    humanUsers,
    rtrUsers,
    aiPercentage,
    humanPercentage,
    rtrPercentage,
    totalPercentage: 100,
    unique_ai_users: uniqueAi,
    unique_human_users: uniqueHuman,
    answers: mergedAnswers,
    freeformAnswers,
    hasFeedback: false,
    questionType,
    summaries,
  };

  return base;
}

function mergeGroupSurveyFlatParsed(
  payloads: SurveyGroupResultsSurveyPayload[],
  showSummaries: boolean,
  parsedByExtSurveyId: Map<string, SurveyStats>,
  summaryByExtSurveyId: Map<string, SurveySummaryData | undefined>
): CombinedQuestionStats[] {
  const out: CombinedQuestionStats[] = [];
  for (const payload of payloads) {
    const stats = parsedByExtSurveyId.get(payload.ext_survey_id);
    if (!stats) {
      continue;
    }
    const summary = showSummaries ? summaryByExtSurveyId.get(payload.ext_survey_id) : undefined;
    for (const q of stats.questions) {
      const sq = summary?.questions.find((s) => s.id === q.id);
      out.push(toCombinedQuestionStats(q, sq, payload.ext_survey_id));
    }
  }
  return out;
}

/**
 * Parsed stats/summary + optional survey-group preview clusters → one row per logical question with combined numerators and answer bars.
 * Falls back to flattened per-survey rows if preview is missing or no clusters match (still uses SurveyService parsing so answer bars work).
 */
export function mergeGroupSurveyCombinedQuestionsFromPayloads(
  payloads: SurveyGroupResultsSurveyPayload[],
  showSummaries: boolean,
  preview?: SurveyGroupPreview | null
): CombinedQuestionStats[] {
  const parsedByExtSurveyId = new Map<string, SurveyStats>();
  const summaryByExtSurveyId = new Map<string, SurveySummaryData | undefined>();

  for (const payload of payloads) {
    const stats = parsePayloadStatsJson(payload);
    if (stats) {
      parsedByExtSurveyId.set(payload.ext_survey_id, stats);
    }
    if (showSummaries) {
      summaryByExtSurveyId.set(payload.ext_survey_id, parsePayloadSummaryJson(payload.summary));
    }
  }

  if (preview?.questions?.length) {
    const out: CombinedQuestionStats[] = [];
    for (const cluster of preview.questions) {
      const merged = mergeClusterQuestions(cluster, parsedByExtSurveyId, summaryByExtSurveyId);
      if (merged) {
        out.push(merged);
      }
    }
    if (out.length > 0) {
      return out;
    }
  }

  return mergeGroupSurveyFlatParsed(payloads, showSummaries, parsedByExtSurveyId, summaryByExtSurveyId);
}

/** Prefer server-merged stats when the backend cutover serves `merged_stats`. */
export function combinedQuestionsFromGroupResultsPayload(
  payload: SurveyGroupResultsPayload,
  showSummaries: boolean,
  preview?: SurveyGroupPreview | null,
): CombinedQuestionStats[] {
  const merged = parseMergedGroupStats(payload);
  if (merged) {
    return merged.questions.map((question) => toCombinedQuestionStats(question, undefined));
  }
  return mergeGroupSurveyCombinedQuestionsFromPayloads(payload.surveys, showSummaries, preview);
}
